import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Link, Paper, Stack, Step, StepLabel, Stepper, Typography,
} from '@mui/material';
import { VerifiedUser, OpenInNew } from '@mui/icons-material';
import listingService from '../../services/listingService';
import { brand, text } from '../../theme/colors';
import { formatHarareDateShort } from '../../utils/time';

const PAY_COPY = 'Your dossier is approved. The next step is an independent Eqaya Verification: a GPS-logged site visit, deeds search, rates re-check and price opinion.';
const REVERIFY_COPY = 'Re-verification needed (details changed or the last verification is older than 12 months).';
const AWAITING_COPY = 'Payment received — waiting for Eqaya to assign a verifier';
const STEPS = ['Paid', 'Verifier assigned', 'Visit done', 'Eqaya sign-off'];
const VERIFIED_STATUSES = ['verified', 'published', 'unpublished', 'sold'];
const POLL_MS = 30000;
const STALE_MS = 365 * 24 * 60 * 60 * 1000;

const CONFIRMS = {
  publish: { title: 'Publish this listing?', body: 'Buyers will see the property, the Eqaya Verified badge and your agency details.', label: 'Publish', color: 'primary' },
  unpublish: { title: 'Hide this listing from buyers?', body: 'It stays verified — you can publish it again at any time.', label: 'Unpublish', color: 'warning' },
  sold: { title: 'Mark this property as sold?', body: 'The listing closes for good. This cannot be undone.', label: 'Mark as sold', color: 'error' },
};

const asDate = (v) => { const d = v ? new Date(v) : null; return d && !Number.isNaN(d.getTime()) ? d : null; };
const isStale = (v) => { const d = asDate(v); return !!d && Date.now() - d.getTime() > STALE_MS; };

/**
 * The realtor's Eqaya Verification block: pay for the check, watch it land, and
 * once it has, control whether buyers can see the listing. Everything it needs
 * beyond the listing itself comes from GET /listings/:id/verification.
 */
export default function VerificationPanel({ listing, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  // The dialog keeps rendering through MUI's close transition, so the copy has
  // to survive `confirm` going null or the box empties as it fades out.
  const [lastConfirm, setLastConfirm] = useState('publish');
  const [searchParams, setSearchParams] = useSearchParams();
  const [cancelled, setCancelled] = useState(false);
  const mounted = useRef(true);
  const status = listing?.status;
  const requestId = listing?.verification_request_id;
  const listingId = listing?.id;

  // Re-arm on mount: StrictMode mounts twice, and a ref left false after the
  // first teardown would freeze the panel on its loading state.
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    try {
      const r = await listingService.verification(listingId);
      if (mounted.current) { setData(r.data); setError(''); }
    } catch (e) {
      // A failed poll must not wipe the panel the realtor is reading.
      if (mounted.current && !quiet) setError(e.response?.data?.error || 'Could not load the verification status');
    } finally {
      if (mounted.current) { setRefreshing(false); setLoading(false); }
    }
    // Re-read whenever the listing moves: paying, a verifier landing and the
    // admin's decision all change what this panel should say.
  }, [listingId, status, requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  // Assignment and the visit happen while the realtor watches; nothing pushes
  // them, so the steps only move if the panel asks again.
  useEffect(() => {
    if (status !== 'verification_pending') return undefined;
    const t = setInterval(() => refresh({ quiet: true }), POLL_MS);
    return () => clearInterval(t);
  }, [status, refresh]);

  // One-shot: show the cancellation once, then drop the flag from the url so a
  // refresh or a later visit does not replay it.
  useEffect(() => {
    if (searchParams.get('verification') !== 'cancelled') return;
    setCancelled(true);
    const next = new URLSearchParams(searchParams);
    next.delete('verification');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const pay = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await listingService.startVerificationCheckout(listingId);
      if (r.data?.checkoutUrl) {
        // Leaving for Stripe: keep the button disabled so the redirect cannot be
        // double-clicked into a second session.
        window.location.assign(r.data.checkoutUrl);
        return;
      }
      setNotice('Payment already received — an Eqaya verifier will be offered your property shortly.');
      setBusy(false);
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not start the payment. Please try again in a moment.');
      setBusy(false);
    }
  };

  const ask = (which) => { setLastConfirm(which); setConfirm(which); };

  const runConfirmed = async () => {
    const which = confirm; setConfirm(null);
    try {
      setBusy(true); setError(''); setNotice('');
      if (which === 'publish') await listingService.publish(listingId);
      else if (which === 'unpublish') await listingService.unpublish(listingId);
      else await listingService.markSold(listingId);
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e.response?.data?.error || 'That did not work. Please try again.');
    } finally { setBusy(false); }
  };

  const request = data?.request || null;
  const pkg = data?.package || null;
  const reason = listing?.unpublish_reason || null;
  // A request that is already paid (or already moving) must never be offered for
  // payment again — the webhook that advances the listing can be seconds behind
  // the realtor's return from Stripe, and a second click would charge twice.
  const settled = !!request && (request.paymentStatus === 'paid' || !['pending', 'cancelled'].includes(request.status));
  const awaitingAssignment = status === 'desk_approved' && settled;
  const stale = isStale(listing?.verified_at);
  // The request attached to a re-verification or a stale listing is the spent
  // one from last time, so `settled` says nothing about the new fee.
  const payable = (status === 'desk_approved' && !settled)
    || (status === 'unpublished' && reason === 'reverification')
    || ((status === 'verified' || (status === 'unpublished' && reason !== 'eac_standing')) && stale);
  const showVerified = VERIFIED_STATUSES.includes(status) && !(status === 'unpublished' && reason === 'reverification');
  const canPublish = !stale && (status === 'verified' || (status === 'unpublished' && !['eac_standing', 'reverification', 'mandate_expired'].includes(reason)));
  // The same Harare-anchored string the buyer reads on the public listing, so a
  // realtor and their buyer never quote different verification dates to each other.
  const verifiedOn = formatHarareDateShort(listing?.verified_at);
  const activeStep = request?.visitCompletedAt ? 3 : request?.assignedAt ? 2 : 1;
  const dialog = CONFIRMS[confirm || lastConfirm];

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <VerifiedUser sx={{ color: brand.primary }} />
        <Typography variant="h6" sx={{ flex: 1 }}>Eqaya Verification</Typography>
        {refreshing && <CircularProgress size={16} sx={{ color: brand.primary }} />}
      </Stack>

      {cancelled && payable && <Alert severity="info" sx={{ mb: 2 }}>Payment was cancelled — nothing was charged.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {loading && !data && <Box sx={{ py: 1 }}><CircularProgress size={22} sx={{ color: brand.primary }} /></Box>}

      {!!data && payable && (
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" sx={{ color: text.secondary }}>{status === 'desk_approved' ? PAY_COPY : REVERIFY_COPY}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {pkg ? `USD ${pkg.priceUsd}` : 'Price to be confirmed'} · non-refundable if the property fails
          </Typography>
          {pkg?.turnaroundHours ? <Typography variant="caption" sx={{ color: text.tertiary }}>Usually completed within {pkg.turnaroundHours} hours of a verifier accepting.</Typography> : null}
          <Button variant="contained" disabled={busy} onClick={pay} sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}>
            Pay for Eqaya Verification
          </Button>
        </Stack>
      )}

      {!!data && awaitingAssignment && (
        <Typography variant="body2" sx={{ color: text.secondary }}>{AWAITING_COPY}</Typography>
      )}

      {!!data && status === 'verification_pending' && (
        <Stack spacing={1.5}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: 13 } }}>
            {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
          <Typography variant="body2" sx={{ color: text.secondary }}>
            {request?.visitCompletedAt
              ? 'Eqaya is reviewing the findings.'
              : request?.assignedAt
                ? 'A verifier has taken the job and will visit the property.'
                : 'Paid. We are matching your property with an Eqaya verifier.'}
          </Typography>
          {request?.id && <Link component={RouterLink} to={`/property-check/c/${request.id}`} underline="hover" sx={{ fontSize: 14, alignSelf: 'flex-start' }}>Open the verification</Link>}
        </Stack>
      )}

      {!!data && showVerified && (
        <Stack spacing={1.5} alignItems="flex-start" sx={{ mt: payable ? 2 : 0 }}>
          {verifiedOn
            ? <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>Eqaya Verified on {verifiedOn}</Typography>
            : <Typography variant="body2" sx={{ color: text.secondary }}>This listing was verified by Eqaya.</Typography>}
          {stale && <Typography variant="caption" sx={{ color: text.tertiary }}>That verification is more than 12 months old, so the listing needs a fresh one before it can go back up.</Typography>}
          {request?.reportUrl && (
            // The report is served by the API, not the SPA — a plain anchor, so
            // the browser fetches the PDF with the session cookies.
            <Link href={request.reportUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              Open the verification report <OpenInNew sx={{ fontSize: 15 }} />
            </Link>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canPublish && <Button variant="contained" disabled={busy} onClick={() => ask('publish')} sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}>Publish</Button>}
            {status === 'published' && <Button variant="outlined" color="warning" disabled={busy} onClick={() => ask('unpublish')}>Unpublish</Button>}
            {(status === 'published' || status === 'unpublished') && <Button variant="outlined" color="inherit" disabled={busy} onClick={() => ask('sold')}>Mark as sold</Button>}
          </Stack>
          {status === 'unpublished' && reason === 'eac_standing' && (
            <Typography variant="body2" sx={{ color: text.secondary }}>Hidden until your agency&apos;s EAC standing is restored</Typography>
          )}
          {status === 'unpublished' && reason === 'mandate_expired' && (
            <Alert severity="warning">
              Hidden from buyers: the owner&apos;s mandate to sell has run out. Upload the renewed mandate under &quot;Mandate to sell from the owner&quot; below. The listing goes back up once Eqaya has checked it with the owner.
            </Alert>
          )}
        </Stack>
      )}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} aria-labelledby="verification-confirm-title" aria-describedby="verification-confirm-body">
        <DialogTitle id="verification-confirm-title">{dialog?.title}</DialogTitle>
        <DialogContent><Typography id="verification-confirm-body">{dialog?.body}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={dialog?.color} disabled={busy} onClick={runConfirmed}>{dialog?.label}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
